import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { DomainReference, DomainTree, Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';

export interface PerformLoadOptions {
  bypassCache: boolean;
}

export interface ClientStore {
  clientTreeMap: Ref<Map<number, DomainTree>>;
  clients: Ref<Loadable<DomainTree[]>>;
  flatClients: Ref<DomainReference[]>;
  hasDomainOptions: Ref<boolean>;
  loadClients: (opts?: PerformLoadOptions) => Promise<void>;
}

const PERFORM_LOAD_DEFAULT_OPTIONS: PerformLoadOptions = {
  bypassCache: false,
};

const _useClientStore = defineStore('client', (): ClientStore => {
  const { selectedDomainId } = useCurrentUserStore();
  const { t } = useI18n();
  const toast = useToast();

  const clients: Ref<Loadable<DomainTree[]>> = ref({
    loading: false,
  });

  const clientTreeMap = computed(() => {
    return DomainTree.toMap(clients.value.data ?? []);
  });

  const flatClients = computed(() => {
    const domainTrees = [...clientTreeMap.value.values()];
    return domainTrees.map((d) => d.asDomainReference());
  });

  const hasDomainOptions = computed(() => {
    const domainTrees = clients.value.data ?? [];
    return domainTrees.length > 1 || !!domainTrees[0]?.children?.length;
  });

  const loadClients = async (
    opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
  ) => {
    const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };

    const numClients = clients.value.data?.length ?? 0;
    const isLoading = clients.value.loading;
    const requiresLoad = numClients === 0 && !isLoading;

    if (options.bypassCache || requiresLoad) {
      return performLoad();
    }
  };

  const performLoad = async () => {
    try {
      clients.value.loading = true;
      clients.value.data = await api.client.getAllSummaries();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      clients.value.error = e;
      console.error('Error fetching client summaries', e);
      toast.addError({
        detail: t('errors.loadingParam', { param: t('clients.clients', 2) }),
        error: e,
      });
    } finally {
      clients.value.loading = false;
    }
  };

  watch(selectedDomainId, () => {
    clients.value.data = [];
  });

  return {
    clientTreeMap,
    clients,
    flatClients,
    hasDomainOptions,
    loadClients,
  };
});

export const useClientStore = (): ClientStore => {
  const store = _useClientStore();
  const { clientTreeMap, clients, flatClients, hasDomainOptions } =
    storeToRefs(store);
  const { loadClients } = store;

  return {
    clientTreeMap,
    clients,
    flatClients,
    hasDomainOptions,
    loadClients,
  };
};
